import useFetchMorePagination from '@@partner:helpers/hooks/useFetchMorePagination'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { renderLazyPickerMenu } from '@components/common/pickers'
import React, { useCallback } from 'react'
import { Button, SelectPicker } from 'rsuite'
import { blockTypes } from './EditorItem'
import { MainPageBlockForm } from '../form/MainPageConfigFormState'
import { AllBannerTypesQuery, useFetchAllBannerTypes } from '@@partner:graphql/query/banners/allBannerTypes.gql'
import EntityLink from './EntityLink'
import EditIcon from '@rsuite/icons/Edit'
import { ActiveEditorState } from '@@partner:containers/marketing/mainPageConfig/MainPageConfigFormPage'


type BannerBlockProps = {
  block: MainPageBlockForm
  onChange: (block: MainPageBlockForm) => void
  onOpenEditor?: (editor: ActiveEditorState) => void
}

export default function BannerGalleryBlock ({
  block,
  onChange,
  onOpenEditor
}: BannerBlockProps) {
  const { brandSlug } = usePartner()
  const [ nameFilter, setNameFilter ] = React.useState<string>('')

  const { data, loading, error, fetchMore } = useFetchAllBannerTypes({
    filter: {
      fields: [ 'name' ],
      op: nameFilter.length > 0 ? 'starts_with' : 'unwrap',
      value: [ nameFilter ]
    },
    pager: { 
      limit: 20
    },
    sort: {
      fieldName: 'created_at',
      direction: 0
    }
  })

  const updaterFunction = useCallback((cached: AllBannerTypesQuery, fetched: AllBannerTypesQuery) => {
    const prev = cached?.allBannerTypes?.edges ?? []
    const curr = fetched?.allBannerTypes?.edges ?? []

    return {
      hasMore: curr.length > 0,
      result: {
        allBannerTypes: {
          ...fetched.allBannerTypes,
          edges: [ ...prev, ...curr ]
        }
      }
    }
  }, [])

  const { hasMore, fetchNextPage } = useFetchMorePagination({
    loading,
    page: data?.allBannerTypes.pageInfo.page ?? 1,
    pageVariable: 'pager',
    usePager: true,
    fetchMore,
    updaterFunction,
  })


  const bannerTypes = data?.allBannerTypes.edges.map((banner) => ({
    label: banner.label,
    value: banner.id,
  })) ?? []

  const selectedBannerType = data?.allBannerTypes.edges.find(bt => bt.id === block.payload?.id)

  const onItemsRendered = (props: any) => {
    if (props.visibleStopIndex >= bannerTypes.length - 1 && hasMore) {
      fetchNextPage()
    }
  }

  const handleOpenEditor = useCallback(() => {
    if (selectedBannerType?.code && onOpenEditor) {
      onOpenEditor({
        type: 'bannerGallery',
        bannerTypeCode: selectedBannerType.code,
        bannerTypeName: selectedBannerType.label
      })
    }
  }, [ selectedBannerType?.code, selectedBannerType?.label, onOpenEditor ])

  return (
    <div>
      <h4>Выберите тип галереи:</h4>
      <SelectPicker
        data={blockTypes.find(bt => bt.type === 'bannerGallery')?.subtypes ?? []}
        value={block.subtype}
        onSelect={(value) => {
          if (onChange && value) {
            onChange({
              ...block,
              subtype: value,
            })
          }
        }}
        placeholder="Выберите тип галереи"
        style={{ marginBottom: 16 }}
      />
      <h4>Выберите группу баннеров:</h4>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <SelectPicker
          data={bannerTypes}
          value={block.payload?.id}
          loading={loading && !data}
          onSelect={(value) => {
            if (onChange && value) {
              const bannerType = data?.allBannerTypes.edges.find(bt => bt.id === value)
              onChange({
                ...block,
                payload: {
                  id: value,
                  name: bannerType?.label,
                }
              })
            }
          }}
          onSearch={setNameFilter}
          renderMenu={renderLazyPickerMenu(loading, data)}
          placeholder="Выберите группу баннеров"
          listProps={{
            onItemsRendered
          }}
          style={{ flex: 1 }}
        />
        <EntityLink
          entityId={block.payload?.id}
          entityType="bannerType"
          brandSlug={brandSlug}
          tooltip="Перейти к типу баннеров"
        />
      </div>

      {block.payload?.id && onOpenEditor && (
        <Button
          appearance="ghost"
          size="sm"
          startIcon={<EditIcon />}
          onClick={handleOpenEditor}
          style={{ marginTop: 12 }}
        >
          Редактировать баннеры галереи
        </Button>
      )}
    </div>
  )
}